import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MiscUtilService } from '@common-services/util/misc-util.service';
import { MatMultiSortDirective } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort.directive';
import { MatMultiSortTableDataSource } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort-data-source';
import { TableData } from '@dice/app/components/organisms/mat-multi-sort/table-data';
import { provideFilterComponent } from '@dice/app/directives/table-filter.directive';
import { provideSortComponent } from '@dice/app/directives/table-sort.directive';
import {
  Column,
  FilterPredicateFn,
  HeaderContent,
  RowContent,
  TableColumnFilter,
  TableConfiguration,
  TableFilterInterface,
  TableSortDefinition,
} from '@dice/app/types/table.types';
import { has as _has } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  selector: 'multisort-table-component',
  templateUrl: './multisort-table.component.html',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'flex' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [provideSortComponent(MultisortTableComponent), provideFilterComponent(MultisortTableComponent)],
  standalone: false,
})
export class MultisortTableComponent<T>
  implements TableFilterInterface<T>, OnInit, AfterViewInit, OnChanges, OnDestroy
{
  private miscUtilService = inject(MiscUtilService);

  @ViewChild(MatMultiSortDirective) sort: MatMultiSortDirective;

  @Input() data: T[];

  @Input() columns: Column[];

  @Input() displayedColumnNames: string[];

  @Input() tableDefinition: TableConfiguration;

  @Input() selectedElement: T;

  @Output() selectedElementChange: EventEmitter<T> = new EventEmitter<T>();

  @Output() mouseOverElementChange: EventEmitter<T> = new EventEmitter<T>();

  @Output() sortChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

  @Input() identityComparator: string;

  @Input() selectedRowClass: string;

  @Input() disableFilter: boolean = false;

  @Input() filterPredicate: FilterPredicateFn<T>;

  @Input() tableColumnFilters: TableColumnFilter[];

  @Input() set searchTerm(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  @Input() defaultSorting: TableSortDefinition;

  @Input() disableSort: boolean;

  @Input() stickyHeader: boolean = false;

  @Input() localStorageKey: string = '';

  dataSource: MatMultiSortTableDataSource<T> = new MatMultiSortTableDataSource(null);

  table: TableData<T>;

  readonly hasDetailsRow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly displayedColumnNames$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  readonly searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private readonly unsubscriber$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.initColumns();
    this.initDetailsRow();
  }

  ngAfterViewInit(): void {
    const columns = [];

    this.columns.forEach((e) => {
      // eslint-disable-next-line dot-notation
      const displayValue = e.table['displayValue'];

      columns.push({ id: displayValue });
    });

    this.table = new TableData<T>(columns, { localStorageKey: this.localStorageKey });

    this.table.dataSource = this.dataSource;
    this.table.dataSource._multiSort = this.sort;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { data, columns, displayedColumnNames } = changes;
    if (data && this.table) {
      this.dataSource.data = this.data || [];
      this.table.initialize();
      this.table.orderData();
    }
    if (columns || displayedColumnNames) {
      this.initColumns();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next();
  }

  private initColumns(): void {
    const names: string[] = this.displayedColumnNames || (this.columns || []).map((column: Column) => column.name);
    this.displayedColumnNames$.next(names);
  }

  private initDetailsRow(): void {
    const hasDetailsRow = _has(this, 'tableDefinition.detailsRow') && !!this.tableDefinition.detailsRow;
    this.hasDetailsRow$.next(hasDetailsRow);
  }

  getRowClass(rowData: T): string {
    if (!_has(this, 'tableDefinition.tableRow.cssClass')) {
      return '';
    }
    const {
      tableRow: { cssClass },
    } = this.tableDefinition;
    return this.getUpdatedCssClasses(cssClass, rowData);
  }

  getColumnCssClass(table: HeaderContent | RowContent, rowData: T): string | string[] {
    if (!_has(table, 'columnCssClass')) {
      return '';
    }
    const { columnCssClass } = table;
    return this.getUpdatedCssClasses(columnCssClass, rowData);
  }

  private getUpdatedCssClasses(cssClassDefinition: string | string[] | ((rowData: T) => string), rowData: T): string {
    if (cssClassDefinition) {
      if (typeof cssClassDefinition === 'string') {
        return cssClassDefinition;
      }
      if (typeof cssClassDefinition === 'function') {
        return cssClassDefinition(rowData);
      }
      if (Array.isArray(cssClassDefinition)) {
        return cssClassDefinition.join(' ');
      }
    }
    return '';
  }

  rowClickHandler(rowDataElement: T): void {
    if (_has(this, 'tableDefinition.tableRow.click')) {
      this.tableDefinition.tableRow.click(rowDataElement);
    } else {
      this.toggleSelectRow(rowDataElement);
    }
  }

  private toggleSelectRow(row: T): void {
    this.selectedElement = this.rowIsSelected(row) ? null : row;
    this.selectedElementChange.emit(this.selectedElement);
  }

  mouseOverHandler(rowDataElement: T): void {
    this.mouseOverElementChange.emit(rowDataElement);
  }

  getSelectedRowClass(row: T): string {
    if (this.rowIsSelected(row) && !!this.selectedRowClass) {
      return this.selectedRowClass;
    }
    return 'table-row';
  }

  rowIsSelected(row: T): boolean {
    return (
      this.selectedElement &&
      this.identityComparator &&
      row[this.identityComparator] === this.selectedElement[this.identityComparator]
    );
  }

  private hasNoData(): boolean {
    return !this.dataSource || !this.dataSource.data || this.dataSource.data.length === 0;
  }

  private isAllDataFilteredOut(): boolean {
    // return this.dataSource && this.dataSource.filteredData && this.dataSource.filteredData.length === 0;
    return false;
  }

  isNoDataDisplayed(): boolean {
    return this.hasNoData() || this.isAllDataFilteredOut();
  }

  trackColumnFn = (index: number, obj: T): string => {
    return this.miscUtilService.trackByFn('name', index, obj) as string;
  };

  sortHeaderFor(column: Column): string {
    // eslint-disable-next-line dot-notation
    const displayValue = column.table['displayValue'];

    return displayValue;
  }
}
